import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { X, Download, Share, Plus } from 'lucide-react';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if prompt was dismissed recently
    const dismissedTime = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show for 7 days after dismissal
      }
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show custom instructions after a delay
    if (iOS && !standalone) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  // Don't show if already installed or prompt shouldn't be shown
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-24 left-4 right-4 z-50 animate-fade-in-up md:left-auto md:right-4 md:w-80">
      <Card className="p-4 bg-paper-light shadow-lifted-lg border border-brown/30 relative">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-ink-500 hover:text-ink-700"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-brown/10 flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6 text-brown" />
          </div>
          
          <div className="flex-1 pr-4">
            <h3 className="font-serif font-medium text-ink-900 text-sm mb-1">
              Add to Home Screen
            </h3>
            <p className="text-xs text-ink-500 font-accent mb-3">
              Install the app for quick access anytime, even offline!
            </p>

            {isIOS ? (
              <div className="text-xs text-ink-600 space-y-2">
                <p className="flex items-center gap-2">
                  <span className="bg-sage/20 p-1 rounded">1</span>
                  Tap <Share className="w-4 h-4 inline text-blue-500" /> Share button
                </p>
                <p className="flex items-center gap-2">
                  <span className="bg-sage/20 p-1 rounded">2</span>
                  Scroll & tap <Plus className="w-4 h-4 inline" /> "Add to Home Screen"
                </p>
              </div>
            ) : (
              <Button
                onClick={handleInstall}
                size="sm"
                className="vintage-btn btn-brown text-xs py-2"
              >
                <Download className="w-4 h-4 mr-1" />
                Install App
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InstallPrompt;
